import { FilterOption, FilterOptionType } from "../filterPopover/FilterPopover.types";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types";
import { GetListData } from "../filterPopover/subMenues/SubMenuDataHelper";
import { InputFieldTypeEnum } from "../../../entityTemplates/inputFields/InputFields.types";
import { keepPreviousData } from "@tanstack/react-query";

import { useDotLegalPostGetQueryArray, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

interface FilterDataRequestItem {
    filterId: string;
    values: Array<string>;
    customInputType: InputFieldTypeEnum | undefined;
}

export interface FilterDataRequest {
    filters: Array<FilterDataRequestItem>;
}

const filterBarDataQueryKey = "filter-bar-data";
const emptyFilterBarData: Record<string, Array<IdNamePair>> = {};

// Only list filters that fetch their options need their ids resolved to names; fixedData filters already carry
// the names. Custom fields are keyed by their bare guid on the wire, the "custom-field-" prefix is a client id.
export function buildFilterDataRequest(filterOptions: Array<FilterOption>): FilterDataRequest {
    const filters: Array<FilterDataRequestItem> = [];

    filterOptions.forEach((option) => {
        const isResolvableList = !option.fixedData && (option.type === FilterOptionType.SelectField || option.type === FilterOptionType.RelatedEntityField);
        if (!isResolvableList) {
            return;
        }

        const selectValues = GetListData(option.value);
        if (selectValues.length === 0) {
            return;
        }

        filters.push({
            filterId: option.id.startsWith("custom-field-") ? option.id.replace("custom-field-", "") : option.id,
            values: selectValues,
            customInputType: option.customInputType,
        });
    });

    return { filters };
}

// The payload is part of the key, so a changed selection fetches by itself and two bars on one page (the list and
// the AI chat scope) each get their own names instead of sharing a slot. A new key starts empty, and the bar hides
// its chips while loading, so the previous names are kept on screen until the new ones arrive.
export function useFilterBarData(filterData: Array<FilterOption>, fetchFilterData: boolean) {
    const queryClient = useDotLegalQueryClient();
    const requestPayload = buildFilterDataRequest(filterData);
    const hasData = requestPayload.filters.length > 0;

    const { data, isLoading } = useDotLegalPostGetQueryArray<Record<string, Array<IdNamePair>>>(
        [filterBarDataQueryKey, requestPayload],
        "/filter-data",
        requestPayload,
        {
            enabled: hasData && fetchFilterData,
            placeholderData: keepPreviousData,
        }
    );

    // Written to the slot the next render will read, so a chip save shows its names without waiting for the fetch.
    const setQueryData = (nextFilterOptions: Array<FilterOption>, nextData: Record<string, Array<IdNamePair>>) => {
        queryClient.setQueryDataArray([filterBarDataQueryKey, buildFilterDataRequest(nextFilterOptions)], nextData);
    };

    return {
        data: data ?? emptyFilterBarData,
        isLoading,
        setQueryData,
    };
}
